import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { cn } from "@/shared/lib/utils";
import styles from "./PinInput.module.scss";
/**
 * One-time-code style input. N cells, auto-advance on input, backspace
 * jumps to previous cell when current is empty, paste fills cells from
 * left. `numeric` (default true) restricts to digits.
 */
export const PinInput = ({ length = 6, value, onChange, onComplete, disabled = false, invalid = false, numeric = true, autoFocus = false, ariaLabel = "One-time code", className, }) => {
    const refs = React.useRef([]);
    // Normalize external value to exactly `length` chars padded with empties.
    const cells = React.useMemo(() => {
        const out = [];
        for (let i = 0; i < length; i++)
            out.push(value[i] ?? "");
        return out;
    }, [value, length]);
    React.useEffect(() => {
        if (autoFocus && !disabled) {
            refs.current[0]?.focus();
        }
    }, [autoFocus, disabled]);
    const sanitize = (raw) => {
        if (!numeric)
            return raw;
        return raw.replace(/\D+/g, "");
    };
    const emit = (next) => {
        onChange(next);
        if (next.length === length && onComplete)
            onComplete(next);
    };
    const focusCell = (i) => {
        const el = refs.current[Math.max(0, Math.min(length - 1, i))];
        el?.focus();
        el?.select?.();
    };
    const handleChange = (i, raw) => {
        const cleaned = sanitize(raw);
        if (cleaned.length === 0) {
            // Native clear via empty input — keep current state.
            const next = value.slice(0, i) + value.slice(i + 1);
            emit(next);
            return;
        }
        // If user pasted/typed multiple chars, fill forward.
        let next = value.split("");
        while (next.length < length)
            next.push("");
        for (let c = 0; c < cleaned.length && i + c < length; c++) {
            next[i + c] = cleaned[c];
        }
        const joined = next.join("").slice(0, length);
        emit(joined);
        const focusAt = Math.min(i + cleaned.length, length - 1);
        focusCell(focusAt);
    };
    const handleKeyDown = (i, e) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            const current = value[i] ?? "";
            if (current) {
                const next = value.slice(0, i) + value.slice(i + 1);
                emit(next);
            }
            else if (i > 0) {
                const next = value.slice(0, i - 1) + value.slice(i);
                emit(next);
                focusCell(i - 1);
            }
        }
        else if (e.key === "ArrowLeft") {
            e.preventDefault();
            focusCell(i - 1);
        }
        else if (e.key === "ArrowRight") {
            e.preventDefault();
            focusCell(i + 1);
        }
    };
    const handlePaste = (i, e) => {
        const text = e.clipboardData.getData("text");
        const cleaned = sanitize(text);
        if (!cleaned)
            return;
        e.preventDefault();
        handleChange(i, cleaned);
    };
    return (_jsx("div", { className: cn(styles.wrap, className), role: "group", "aria-label": ariaLabel, children: cells.map((c, i) => (_jsx("input", { ref: (el) => {
                refs.current[i] = el;
            }, type: numeric ? "tel" : "text", inputMode: numeric ? "numeric" : "text", autoComplete: "one-time-code", maxLength: 1, value: c, disabled: disabled, "aria-invalid": invalid || undefined, className: cn(styles.cell, c && styles.cellFilled, invalid && styles.invalid), onChange: (e) => handleChange(i, e.target.value), onKeyDown: (e) => handleKeyDown(i, e), onPaste: (e) => handlePaste(i, e), onFocus: (e) => e.currentTarget.select() }, i))) }));
};
